import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet, TENANT_TOKEN_KEY } from '../../api/client';
import { Badge, Button, Card } from '../../design-system';
import { tokens } from '../../design-system/tokens';

interface AuditEvent {
  id: string;
  eventType: string;
  severity: 'info' | 'warning' | 'error';
  source: string;
  resourceId: string | null;
  bookingId: string | null;
  message: string;
  payloadJson: Record<string, unknown> | null;
  createdAt: string;
}

function severityVariant(severity: string): 'success' | 'warning' | 'error' | 'neutral' {
  if (severity === 'error') return 'error';
  if (severity === 'warning') return 'warning';
  if (severity === 'info') return 'success';
  return 'neutral';
}

export function AgentAuditPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const token = localStorage.getItem(TENANT_TOKEN_KEY);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');

  const loadEvents = useCallback(async () => {
    if (!agentId || !token) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (sourceFilter) params.set('source', sourceFilter);
      if (severityFilter) params.set('severity', severityFilter);
      const data = await apiGet<{ events: AuditEvent[] }>(
        `/api/agents/${agentId}/audit-events?${params.toString()}`,
        token
      );
      setEvents(data.events ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [agentId, token, sourceFilter, severityFilter]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
      <Link
        to="/app/plugins"
        style={{
          display: 'inline-block',
          marginBottom: 12,
          fontSize: 14,
          color: tokens.colors.neutral[600],
          textDecoration: 'none',
        }}
      >
        ← Volver a plugins
      </Link>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, color: tokens.colors.neutral[900] }}>Auditoría</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: tokens.colors.neutral[500] }}>
            Eventos operativos del agente: sync iCal, integraciones y más.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void loadEvents()}>
          Actualizar
        </Button>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: tokens.colors.neutral[600] }}>
          Fuente{' '}
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            style={{ marginLeft: 6, padding: '6px 8px', borderRadius: 8, border: `1px solid ${tokens.colors.neutral[200]}` }}
          >
            <option value="">Todas</option>
            <option value="ical">iCal</option>
            <option value="calendar">Calendario</option>
            <option value="agent">Agente</option>
          </select>
        </label>
        <label style={{ fontSize: 13, color: tokens.colors.neutral[600] }}>
          Severidad{' '}
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            style={{ marginLeft: 6, padding: '6px 8px', borderRadius: 8, border: `1px solid ${tokens.colors.neutral[200]}` }}
          >
            <option value="">Todas</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
          </select>
        </label>
      </div>

      {error ? <p style={{ color: tokens.colors.error.dark }}>{error}</p> : null}
      {loading ? <p style={{ color: tokens.colors.neutral[500] }}>Cargando eventos…</p> : null}

      {!loading && events.length === 0 ?
        <Card>
          <p style={{ margin: 0, color: tokens.colors.neutral[500] }}>No hay eventos para mostrar.</p>
        </Card>
      : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {events.map((ev) => (
          <Card key={ev.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                  <Badge variant={severityVariant(ev.severity)} size="sm">
                    {ev.severity}
                  </Badge>
                  <Badge variant="neutral" size="sm">
                    {ev.source}
                  </Badge>
                  <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>
                    {new Date(ev.createdAt).toLocaleString('es')}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: 14, color: tokens.colors.neutral[900] }}>{ev.message}</p>
                {ev.payloadJson ?
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ fontSize: 12, color: tokens.colors.neutral[500], cursor: 'pointer' }}>
                      Detalle técnico
                    </summary>
                    <pre
                      style={{
                        marginTop: 8,
                        padding: 10,
                        borderRadius: 8,
                        background: tokens.colors.neutral[50],
                        fontSize: 11,
                        overflow: 'auto',
                      }}
                    >
                      {JSON.stringify(ev.payloadJson, null, 2)}
                    </pre>
                  </details>
                : null}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default AgentAuditPage;
